import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { ComunicadoService, WhiteLabelStateService } from 'app/shared/services/edduca-imob';
import { interval, startWith, Subject, takeUntil } from 'rxjs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from 'app/core/auth/auth.service';
import { CommonModule } from '@angular/common';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';
import { FooterPoweredComponent } from 'app/shared/components/footer-powered/footer-powered.component';

@Component({
    selector: 'compact-layout',
    templateUrl: './compact.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatBadgeModule,
        MatTooltipModule,
        UserComponent,
        RouterOutlet,
        FuseVerticalNavigationComponent,
        FooterPoweredComponent
    ],
})
export class CompactLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    unreadCount = 0;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    usuarioId: number;
    perfilId: number;
    planoId: number | null = null;

    protected readonly wlState = inject(WhiteLabelStateService);
    private readonly logoEdducaMenu = 'assets/img/logo/secundario.png';

    /** Logo do menu lateral: do WL se houver, senão a oficial. */
    get logoMenu(): string {
        return this.wlState.current()?.logoMenuUrl || this.logoEdducaMenu;
    }

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _comunicadoService: ComunicadoService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _authService: AuthService
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Verifica se deve mostrar o badge / botão de Comunicados.
     * Regras:
     *   - ADM_SISTEMA e PARCEIRO não recebem comunicados.
     *   - USUARIO_IMOBILIARIA no plano 1 (básico) não tem acesso a Comunicados.
     */
    get showBadge(): boolean {
        if (this.perfilId === PerfilEnum.ADM_SISTEMA || this.perfilId === PerfilEnum.PARCEIRO) return false;
        if (this.perfilId === PerfilEnum.USUARIO_IMOBILIARIA && this.planoId === 1) return false;
        return true;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Obtém informações do token (inclui planoId — usado para esconder Comunicados no plano básico)
        const tokenInfo = this._authService.getTokenInfo();
        if (tokenInfo) {
            this.usuarioId = Number(tokenInfo.userId);
            this.perfilId = Number(tokenInfo.perfilId);
            const rawPlanoId = tokenInfo.planoId ?? tokenInfo.PlanoId ?? tokenInfo.plano_id;
            this.planoId = rawPlanoId != null && rawPlanoId !== '' ? Number(rawPlanoId) : null;

            // Carrega white-label aplicável (hierarquia imobiliária Enterprise → cupom parceiro).
            // Sobrescreve o WL detectado pré-login pelo subdomínio quando aplicável ao usuário.
            this.wlState.carregarAplicavel();
        }

        // Configura a escuta do BehaviorSubject de contagem
        this._comunicadoService.unreadCount$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(count => {
                this.unreadCount = count;
                this._changeDetectorRef.markForCheck();
            });

        // Só inicia o polling de comunicados para perfis que recebem comunicados
        if (this.usuarioId && this.showBadge) {
            // Polling a cada 60 segundos + refresh inicial
            interval(60000).pipe(
                startWith(0),
                takeUntil(this._unsubscribeAll)
            ).subscribe(() => {
                this._comunicadoService.refreshUnreadCount(this.usuarioId);
            });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    verComunicados() {
        this._router.navigate(['/comunicados']);
    }

    home() {
        this._router.navigate(['/inicio']);
    }
}
